import { Button, Group, Stack, TextInput } from '@mantine/core'
import { Select } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import { type GetUsersResponseEntry } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'

import type { AllowedUserRole } from './AddUserModal'
import type { UserFormValues } from './types'
import { validateEmail, validatePassword } from './utils'

interface EditUserModalProps {
  closeModal: () => void
  user: GetUsersResponseEntry
  role?: AllowedUserRole
  entityName: string
}

interface UpdateUserResponse {
  success?: boolean
  errors?: unknown
}

export function EditUserModal({ closeModal, user, role, entityName }: EditUserModalProps) {
  const { api, uiStore } = useGlobalState()
  const [isProcessing, setIsProcessing] = useState(false)

  const canSelectRole = role == undefined

  // Determine user's current role based on their permissions
  const getUserRole = (): UserFormValues['role'] => {
    if (user.isSponsorAdmin) return 'Admin'
    if (user.smeAccess) return 'SME'
    return 'User'
  }

  const form = useForm<UserFormValues>({
    initialValues: {
      name: user.name || '',
      email: user.email,
      password: '',
      role: getUserRole(),
      topicIds: [],
    },
    validate: {
      email: validateEmail,
      password: validatePassword,
    },
  })

  const updateUserMutation = useMutation<
    UpdateUserResponse,
    Error,
    {
      email: string
      user_id: string
      name?: string
      sme: boolean
      is_user_disabled: boolean
      sponsor_admin: boolean
      password?: string
    }
  >({
    mutationFn: api.updateUser,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => uiStore.hideLoadingOverlay(),
    onSuccess: response => {
      const success = 'success' in response && response.success
      const error = 'errors' in response
      if (success) {
        api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
        closeModal()
        notifications.show({
          title: 'Success',
          message: `${entityName} updated successfully`,
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      } else if (error) {
        console.log('error', response)
        notifications.show({
          title: 'Error',
          message: `Failed to update ${entityName}. Please try again.`,
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
      setIsProcessing(false)
    },
  })

  const handleSubmit = (values: UserFormValues) => {
    setIsProcessing(true)
    updateUserMutation.mutate({
      email: values.email,
      user_id: user.userId,
      name: values.name,
      sme: values.role === 'SME',
      sponsor_admin: values.role === 'Admin',
      is_user_disabled: user.status === 'disabled',
      password: values.password || undefined, // Only include password if it was changed
    })
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Group gap="sm" align="space-between">
          <TextInput
            required
            label="Email"
            placeholder="user@example.com"
            flex={1}
            {...form.getInputProps('email')}
            disabled={true}
          />
          {canSelectRole && (
            <Select
              required
              label="Role"
              data={[
                { value: 'User', label: 'User' },
                { value: 'SME', label: 'Expert' },
                { value: 'Admin', label: 'Program Manager' },
                // { value: 'SWI Admin', label: 'SWI Admin' },
              ]}
              {...form.getInputProps('role')}
              style={{ width: 200 }}
            />
          )}
        </Group>
        <TextInput label="Name" placeholder="John Smith" {...form.getInputProps('name')} />
        {/* <TextInput
          type="password"
          label="Password"
          placeholder="(Leave blank to keep current password)"
          style={{ flex: 1 }}
          {...form.getInputProps('password')}
        /> */}
        <Group justify="flex-end" mt="xl">
          <Button variant="light" onClick={closeModal} disabled={isProcessing}>
            Cancel
          </Button>
          <Button type="submit" loading={isProcessing}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
